// The confirmation link from the trial sign-up email (TGCLOUD-1844). Choosing
// a password here is what creates the workspace.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import AuthLayout from 'src/components/UI/auth/AuthLayout';
import PasswordInput, {
  PasswordStrengthAlert
} from 'src/components/UI/input/PasswordInput';
import { validatePasswordStrength } from 'src/util/util';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const MIN = 10;

export default function TrialVerify() {
  const t = useAutoTranslation();

  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [pending, setPending] = React.useState(null); // null loading, false invalid
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    axios
      .post(APICONSTANTS.trialVerify, { token })
      .then((res) => setPending(res.data))
      .catch(() => setPending(false));
  }, [token]);

  const ok =
    password.length >= MIN &&
    password === confirm &&
    validatePasswordStrength(password);

  const submit = (e) => {
    e.preventDefault();
    if (!ok || busy) return;
    setBusy(true);
    setError(null);
    axios
      .post(APICONSTANTS.trialComplete, { token, password })
      .then(() => {
        setDone(true);
        setTimeout(() => navigate(CONSTANTS.signinPath), 4000);
      })
      .catch((err) =>
        setError(
          err?.response?.data?.message ||
            t('Your workspace could not be created. Please try again.')
        )
      )
      .finally(() => setBusy(false));
  };

  return (
    <AuthLayout variant="narrow">
      <Box sx={{ width: '100%' }}>
        {pending === null && <CircularProgress size={24} />}
        {pending === false && (
          <Alert severity="error">
            {t(
              'This link has expired or has already been used. Sign up again, or if you have already created your workspace, sign in.'
            )}
          </Alert>
        )}
        {done && (
          <Alert severity="success">
            {t('Your workspace for')} <b>{pending?.organisation}</b>{' '}
            {t('is ready. Taking you to sign in…')}
          </Alert>
        )}
        {pending && !done && (
          <form onSubmit={submit}>
            <Stack spacing={2}>
              <Typography variant="h6">
                {t('Welcome, {{firstName}}', { firstName: pending.firstName })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('Choose a password for')} {pending.email}{' '}
                {t('to create the')} <b>{pending.organisation}</b>{' '}
                {t('workspace.')}
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <PasswordInput
                name="password"
                label={t('Password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrengthAlert password={password} />
              <PasswordInput
                name="confirm"
                label={t('Confirm password')}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                error={!!confirm && confirm !== password}
                helperText={
                  confirm && confirm !== password
                    ? t('The passwords do not match')
                    : t('At least {{min}} characters', { min: MIN })
                }
              />
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!ok || busy}
              >
                {busy ? (
                  <CircularProgress size={20} />
                ) : (
                  t('Create my workspace')
                )}
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </AuthLayout>
  );
}
